import { CheckIcon } from "@chakra-ui/icons";
import { Input, InputGroup, InputRightElement } from "@chakra-ui/react";
import React from "react";
import {
  BaseFieldProps,
  FieldWrapper,
} from "../fields/FieldWrapper/FieldWrapper";
import DateUtil from "../../util/DateUtil";

interface DateTimeFieldProps extends BaseFieldProps {
  isValid?: boolean;
  timezoneAgnostic?: boolean;
  minDate?: Date;
  maxDate?: Date;
  step?: number;
}

export const DateTimeField = ({
  name,
  label,
  id,
  helperText,
  isValid,
  infoText,
  isRequired,
  timezoneAgnostic = false,
  minDate,
  maxDate,
  step,
}: DateTimeFieldProps) => {
  return (
    <FieldWrapper
      name={name}
      id={id}
      label={label}
      helperText={helperText}
      infoText={infoText}
      isRequired={isRequired}
    >
      {({ field, form }) => {
        // Convert Date object to datetime-local format (YYYY-MM-DDTHH:mm)
        const dateToInputValue = (date: Date | null): string => {
          if (!date) return "";

          const pad = (n: number) => String(n).padStart(2, "0");
          return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
            date.getDate()
          )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
        };

        // Convert datetime-local format to Date object
        const inputValueToDate = (value: string): Date | null => {
          if (!value) return null;

          // Parse the datetime-local format: YYYY-MM-DDTHH:mm
          const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
          if (!match) return null;

          const [, year, month, day, hours, minutes] = match;
          return new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day),
            parseInt(hours),
            parseInt(minutes),
            0 // seconds
          );
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          const dateValue = inputValueToDate(value);

          if (timezoneAgnostic && dateValue) {
            // Store as timezone-agnostic ISO string (YYYY-MM-DDTHH:mm:ss)
            const isoString = DateUtil.dateToTimezoneAgnosticISO(dateValue);
            form.setFieldValue(name, isoString);
          } else {
            form.setFieldValue(name, dateValue);
          }
        };

        const getValue = (): string => {
          let dateValue: Date | null = null;

          if (timezoneAgnostic && typeof field.value === "string") {
            // Parse timezone-agnostic string without timezone conversion
            dateValue = DateUtil.timezoneAgnosticISOToDate(field.value);
          } else if (field.value instanceof Date) {
            dateValue = field.value;
          } else if (field.value) {
            // Try to parse as date string
            dateValue = new Date(field.value);
            if (isNaN(dateValue.getTime())) {
              dateValue = null;
            }
          }

          return dateToInputValue(dateValue);
        };

        return (
          <InputGroup>
            <Input
              type="datetime-local"
              id={id}
              name={name}
              value={getValue()}
              onChange={handleChange}
              min={minDate ? dateToInputValue(minDate) : undefined}
              max={maxDate ? dateToInputValue(maxDate) : undefined}
              step={step}
              backgroundColor="#fff"
              onBlur={field.onBlur}
            />
            {isValid && (
              <InputRightElement>
                <CheckIcon color="green.400" />
              </InputRightElement>
            )}
          </InputGroup>
        );
      }}
    </FieldWrapper>
  );
};
